import { apisStore } from "context/GlobalStates";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import Apis from "serviceapi/ApiImports";
import NotificationPopUp from "util/NotificationPopUp";
import {DeduplicationConfig, GlobalDeduplicationConfig, ManagementApi, MeterFilterConfig, TestingApi} from "../../serviceapi/dataCollectorApi";

interface DeduplicationProps {
  close(): void;
}

const Deduplication = ({ close }: DeduplicationProps) => {
  const [filteredMeters, setFilteredMeters] = useState<string[]>([]);
  const [deduplicationConfig, setDeduplicationConfig] =
    useState<DeduplicationConfig>({});
  const [meterFilterConfig, setMeterFilterConfig] = useState<MeterFilterConfig>(
    {}
  );

  Apis("datacollector");

  const apiManagement = apisStore.getState()?.dataCollector
    ?.managementApi as ManagementApi;
  const apiTesting = apisStore.getState()?.dataCollector
    ?.testingApi as TestingApi;

  const getGlobalDeduplicationConfig = async () => {
    const globalDeduplicationConfig =
      await apiManagement.getGlobalDeduplicationConfig();
    setDeduplicationConfig({
      enabled: globalDeduplicationConfig.data.deduplicationConfig?.enabled,
      sendWithNoChangeTimeout:
        globalDeduplicationConfig.data.deduplicationConfig
          ?.sendWithNoChangeTimeout,
      epsilon: globalDeduplicationConfig.data.deduplicationConfig?.epsilon,
    });
    setMeterFilterConfig(
      globalDeduplicationConfig.data.meterFilterConfig ?? {}
    );
  };

  const getFilteredMeters = async () => {
    const filteredMeters = await apiTesting.testMeterFilter(
      meterFilterConfig,
      deduplicationConfig.enabled,
      undefined
    );
    setFilteredMeters(filteredMeters.data);
  };

  useEffect(() => {
    getGlobalDeduplicationConfig();
  }, []);

  useEffect(() => {
    getFilteredMeters();
  }, [meterFilterConfig]);

  const deduplicationDialogHeaderTemplate = () => {
    return <div className="text-xl">Deduplication</div>;
  };

  const handleApplyDeduplicationConfig = async () => {
    const globalDeduplicationConfig: GlobalDeduplicationConfig = {
      meterFilterConfig: meterFilterConfig,
      deduplicationConfig: deduplicationConfig,
    };
    try {
      await apiManagement.setGlobalDeduplicationConfig(
        globalDeduplicationConfig
      );
      NotificationPopUp.show(
        "Deduplication config updated successfully!",
        "success"
      );
    } catch (error) {
      NotificationPopUp.show("Failed to update deduplication config.", "error");
    }
  };

  return (
    <Dialog
      visible={true}
      onHide={close}
      style={{ width: "450px", height: "70vh" }}
      header={deduplicationDialogHeaderTemplate}
      contentStyle={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div className="flex flex-column gap-2 w-12">
        <div className="flex flex-row gap-1 w-max align-items-center">
          <label htmlFor="enabled">Enabled:</label>
          <InputSwitch
            id="enabled"
            checked={deduplicationConfig.enabled as boolean}
            onChange={(e) =>
              setDeduplicationConfig({
                ...deduplicationConfig,
                enabled: e.value as boolean,
              })
            }
            className="toggle-button-custom"
            tooltip="Enable deduplication"
          />
        </div>
        <div className="flex flex-row justify-content-between gap-2 w-12">
          <div className="flex flex-column gap-2 w-10">
            <label htmlFor="meters">Meters</label>
            <Chips
              value={meterFilterConfig.meters}
              onChange={(e) =>
                setMeterFilterConfig({
                  ...meterFilterConfig,
                  meters: e.value as string[],
                })
              }
            />
          </div>
          <div className="flex flex-column gap-2 w-2 align-items-center">
            <label htmlFor="useRegex">Regex</label>
            <InputSwitch
              checked={meterFilterConfig.useRegex as boolean}
              onChange={(e) =>
                setMeterFilterConfig({
                  ...meterFilterConfig,
                  useRegex: e.value as boolean,
                })
              }
              className="toggle-button-custom"
              tooltip="Use regex to filter meters"
            />
          </div>
        </div>
        <div className="flex flex-row justify-content-between w-12 gap-2">
          <div className="flex flex-row gap-2 w-10">
            <div className="flex flex-column gap-2 w-6">
              <label htmlFor="timeOut">TimeOut</label>
              <InputText
                id="timeOut"
                className="w-full"
                value={deduplicationConfig.sendWithNoChangeTimeout ?? ""}
                onChange={(e) =>
                  setDeduplicationConfig({
                    ...deduplicationConfig,
                    sendWithNoChangeTimeout: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-column gap-2 w-6">
              <label htmlFor="epsilon">Epsilon</label>
              <div className="flex flex-row gap-2">
                <InputNumber
                  id="epsilon"
                  className="w-full"
                  value={deduplicationConfig.epsilon ?? 0}
                  maxFractionDigits={2}
                  onChange={(e) =>
                    setDeduplicationConfig({
                      ...deduplicationConfig,
                      epsilon: e.value as number,
                    })
                  }
                />
              </div>
            </div>
          </div>
          <div className="w-2 flex justify-content-center align-self-end">
            <Button label="Apply" onClick={handleApplyDeduplicationConfig} />
          </div>
        </div>
      </div>
      <Divider />
      {filteredMeters.length > 0 && (
        <div style={{ flex: 1, overflowY: "auto" }} className="mt-3">
          <div className="text-sm font-medium mb-2">Filtered Meters:</div>
          <div className="flex flex-column gap-1">
            {filteredMeters.map((meter, index) => (
              <div
                key={index}
                className="flex justify-content-start align-items-center gap-3 px-2 py-1 border-round"
                style={{
                  backgroundColor: index % 2 === 0 ? "#2e3a3c" : "#232c2e",
                }}
              >
                <span className="font-semibold">{index + 1}.</span>
                <span>{meter}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default Deduplication;
